import type { FormEvent } from 'react';
import { DateTimeInput } from '@/shared/ui/DateTimeInput';
import { FilterActions } from '@/shared/ui/FilterActions';
import { FilterField } from '@/shared/ui/FilterField';
import { FilterSelect } from '@/shared/ui/FilterSelect';
import { MultiSelect } from '@/shared/ui/MultiSelect';
import { SearchKeyInput } from '@/shared/ui/SearchKeyInput';
import type { SelectOption } from '@/shared/ui/selectTypes';
import type { MetrikaFilterOptions, MetrikaFilterValues } from './api';

export type MetrikaFilterName =
  | 'search'
  | 'dateStart'
  | 'dateEnd'
  | 'period'
  | 'device'
  | 'roles'
  | 'source'
  | 'partners'
  | 'os'
  | 'isApp';

type MultiField = 'device' | 'roles' | 'source' | 'partners' | 'os';

const SEARCH_KEY_OPTIONS: SelectOption[] = [
  { value: 'user_id', label: 'Поиск по id' },
  { value: 'user_name', label: 'Поиск по имени' },
  { value: 'phone', label: 'Поиск по телефону' },
  { value: 'email', label: 'Поиск по e-mail' },
];

const IS_APP_OPTIONS: SelectOption[] = [
  { value: '1', label: 'Да' },
  { value: '0', label: 'Нет' },
];

const MULTI_FIELDS: Record<MultiField, { label: string; options: keyof MetrikaFilterOptions }> = {
  device: { label: 'Устройство', options: 'device' },
  roles: { label: 'Роль', options: 'roles' },
  source: { label: 'Источник', options: 'sources' },
  partners: { label: 'Партнеры', options: 'partners' },
  os: { label: 'Операционка', options: 'os' },
};

type Props = {
  /** Controls to show, in order. */
  fields: MetrikaFilterName[];
  options: MetrikaFilterOptions | undefined;
  values: MetrikaFilterValues;
  onChange: <K extends keyof MetrikaFilterValues>(key: K, value: MetrikaFilterValues[K]) => void;
  onSubmit: (e: FormEvent) => void;
  onReset: (e: FormEvent) => void;
};

/** Filter form shared by the users / activity / traffic tabs (legacy `FilterBox` / `FilterBoxMulti` / date pickers). */
export const MetrikaFilterBar = ({ fields, options, values, onChange, onSubmit, onReset }: Props) => {
  const renderField = (name: MetrikaFilterName) => {
    switch (name) {
      case 'search':
        return (
          <SearchKeyInput
            keyOptions={SEARCH_KEY_OPTIONS}
            searchKey={values.searchField}
            searchValue={values.search}
            onKeyChange={(v) => onChange('searchField', v)}
            onValueChange={(v) => onChange('search', v)}
          />
        );
      case 'dateStart':
        return (
          <FilterField label="Дата от">
            <DateTimeInput
              type="date"
              value={values.dateStart}
              max={values.dateEnd || undefined}
              onChange={(v) => onChange('dateStart', v)}
            />
          </FilterField>
        );
      case 'dateEnd':
        return (
          <FilterField label="Дата до">
            <DateTimeInput
              type="date"
              value={values.dateEnd}
              min={values.dateStart || undefined}
              onChange={(v) => onChange('dateEnd', v)}
            />
          </FilterField>
        );
      case 'period':
        return (
          <FilterSelect
            options={options?.periods ?? []}
            value={values.period}
            placeholder="Период"
            onChange={(v) => onChange('period', v)}
          />
        );
      case 'isApp':
        return (
          <FilterSelect
            options={IS_APP_OPTIONS}
            value={values.isApp}
            placeholder="Приложение"
            onChange={(v) => onChange('isApp', v)}
          />
        );
      default: {
        const { label, options: optionsKey } = MULTI_FIELDS[name];
        return (
          <MultiSelect
            options={options?.[optionsKey] ?? []}
            value={values[name]}
            placeholder={label}
            onChange={(v) => onChange(name, v)}
          />
        );
      }
    }
  };

  return (
    <form onSubmit={onSubmit} style={{ marginBottom: 16 }}>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0 12px' }}>
        {fields.map((name) => (
          <div key={name} style={{ marginBottom: 8 }}>
            {renderField(name)}
          </div>
        ))}
      </div>
      <FilterActions onReset={onReset} />
    </form>
  );
};
